import {FC, useEffect, useMemo, useState} from "react";
import classNames from "classnames/dedupe";
import {cn} from "src/helpers/bem";
import "./styles.scss";
import {
  Alert,
  Button,
  Chip,
  Container,
  OutlinedInput,
  Snackbar,
  Switch,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import TuneIcon from "@mui/icons-material/Tune";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
} from "@mui/x-data-grid";
import {useNavigate} from "react-router-dom";
import dayjs from "dayjs";
import {ROUTES} from "src/consts/routes";
import {
  AppHeader,
  AppSelect,
  BannerCarouselSettingsDialog,
  BannerFormDialog,
  ConfirmDialog,
} from "src/components";
import {
  BANNER_PLACEMENTS,
  BANNER_PLATFORMS,
  BANNER_STATUSES,
  bannerLabel,
} from "src/consts/banners";
import {
  BannersListParams,
  useBannersList,
  useDeleteBanner,
  useToggleBanner,
  useUpdateBannerPriority,
} from "src/hooks/banners";
import {BannerModel} from "src/api/generated";

const b = cn("banners-page");

// Сколько названий проектов показывать в ячейке до сворачивания в «+N»
const PROJECTS_IN_CELL = 2;

interface Props {
  className?: string;
}

const PAGE_SIZE = 20;

type Snack = {message: string; severity: "success" | "error"} | null;

const formatDate = (value?: string) =>
  value ? dayjs(value).format("DD.MM.YYYY") : "—";

const errorMessage = (error: unknown, fallback: string) =>
  (error as any)?.response?.data?.error ?? fallback;

export const BannersPage: FC<Props> = (props) => {
  const {className} = props;
  const navigate = useNavigate();

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
  });

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const [placement, setPlacement] = useState("");
  const [platform, setPlatform] = useState("");
  const [status, setStatus] = useState("");

  const [filtersOpen, setFiltersOpen] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<BannerModel | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formBanner, setFormBanner] = useState<BannerModel | null>(null);
  const [carouselOpen, setCarouselOpen] = useState(false);

  const [snack, setSnack] = useState<Snack>(null);
  const showSuccess = (message: string) =>
    setSnack({message, severity: "success"});
  const showError = (message: string) => setSnack({message, severity: "error"});

  const hasFilters = Boolean(search || placement || platform || status);

  // Дебаунс поиска: search триггерит запрос, а не каждый ввод символа
  useEffect(() => {
    const timer = setTimeout(() => {
      setPaginationModel((prev) => ({...prev, page: 0}));
      setSearch(searchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const listParams = useMemo<BannersListParams>(
    () => ({
      page: paginationModel.page + 1, // бэкенд считает страницы с 1
      pageSize: paginationModel.pageSize,
      placement: (placement || undefined) as BannersListParams["placement"],
      platform: (platform || undefined) as BannersListParams["platform"],
      status: (status || undefined) as BannersListParams["status"],
      search: search || undefined,
    }),
    [paginationModel, placement, platform, status, search],
  );

  const {data, isFetching, isError, error} = useBannersList(listParams);

  const toggleMutation = useToggleBanner();
  const priorityMutation = useUpdateBannerPriority();
  const deleteMutation = useDeleteBanner();

  useEffect(() => {
    // Ошибка авторизации — та же модель, что и на остальных страницах
    const statusCode = (error as any)?.response?.status;
    if (statusCode === 403) {
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      navigate(ROUTES.LOGIN);
    }
  }, [error, navigate]);

  const clearFilters = () => {
    setSearchInput("");
    setSearch("");
    setPlacement("");
    setPlatform("");
    setStatus("");
    setPaginationModel((prev) => ({...prev, page: 0}));
  };

  const openCreate = () => {
    setFormBanner(null);
    setFormOpen(true);
  };

  const openEdit = (banner: BannerModel) => {
    setFormBanner(banner);
    setFormOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!deleteTarget?.id) return;
    deleteMutation.mutate(deleteTarget.id, {
      onSuccess: () => {
        setDeleteTarget(null);
        showSuccess("Баннер удалён");
      },
      onError: (e) => showError(errorMessage(e, "Не удалось удалить баннер")),
    });
  };

  const columns: GridColDef<BannerModel>[] = [
    {
      field: "imageUrl",
      headerName: "Превью",
      width: 80,
      sortable: false,
      renderCell: (params) =>
        params.value ? (
          <img
            className={b("preview")}
            src={params.value}
            alt={params.row.title ?? ""}
          />
        ) : (
          <div className={b("preview-empty")} />
        ),
    },
    {field: "title", headerName: "Название", width: 240, sortable: false},
    {
      field: "placements",
      headerName: "Размещение",
      width: 220,
      sortable: false,
      renderCell: (params) => (
        <div className={b("placements")}>
          {(params.value ?? []).map((placementKey: string) => (
            <Chip
              key={placementKey}
              label={bannerLabel(BANNER_PLACEMENTS, placementKey)}
              size="small"
              variant="outlined"
            />
          ))}
        </div>
      ),
    },
    {
      field: "projects",
      headerName: "Проекты",
      width: 260,
      sortable: false,
      renderCell: (params) => {
        const ids = params.row.projectIds ?? [];

        // Пустой projectIds означает «все проекты» — то же правило, по которому
        // режим определяется в форме (см. bannerToForm)
        if (!ids.length) {
          return <span className={b("projects-all")}>Все проекты</span>;
        }

        // projects приходит вместе с баннером; если названия для id нет
        // (например, проект удалён), показываем UUID, а не теряем его молча
        const names = ids.map(
          (id) => (params.row.projects ?? []).find((it) => it.id === id)?.name ?? id,
        );
        const visible = names.slice(0, PROJECTS_IN_CELL);
        const hidden = names.length - visible.length;

        return (
          <div className={b("projects")} title={names.join(", ")}>
            {visible.map((name, index) => (
              <Chip key={ids[index]} label={name} size="small" variant="outlined" />
            ))}
            {hidden > 0 && <Chip label={`+${hidden}`} size="small" />}
          </div>
        );
      },
    },
    {
      field: "isActive",
      headerName: "Активен",
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Switch
          checked={Boolean(params.value)}
          disabled={toggleMutation.isPending}
          onChange={(_, checked) => {
            if (params.row.id) {
              toggleMutation.mutate(
                {id: params.row.id, isActive: checked},
                {
                  onError: (e) =>
                    showError(errorMessage(e, "Не удалось изменить статус")),
                },
              );
            }
          }}
        />
      ),
    },
    {
      field: "priority",
      headerName: "Приоритет",
      width: 130,
      sortable: false,
      renderCell: (params) => {
        const current = params.value ?? 0;
        const changePriority = (delta: number) => {
          if (params.row.id) {
            priorityMutation.mutate(
              {id: params.row.id, priority: Math.max(0, current + delta)},
              {
                onError: (e) =>
                  showError(errorMessage(e, "Не удалось изменить приоритет")),
              },
            );
          }
        };
        return (
          <div className={b("priority")}>
            <span>{current}</span>
            <IconButton
              size="small"
              aria-label="Повысить приоритет"
              disabled={priorityMutation.isPending}
              onClick={() => changePriority(1)}
            >
              <KeyboardArrowUpIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              aria-label="Понизить приоритет"
              disabled={priorityMutation.isPending || current <= 0}
              onClick={() => changePriority(-1)}
            >
              <KeyboardArrowDownIcon fontSize="small" />
            </IconButton>
          </div>
        );
      },
    },
    {
      field: "startsAt",
      headerName: "Начало",
      width: 120,
      sortable: false,
      valueFormatter: (params) => formatDate(params.value as string),
    },
    {
      field: "endsAt",
      headerName: "Окончание",
      width: 120,
      sortable: false,
      valueFormatter: (params) => formatDate(params.value as string),
    },
    {
      field: "actions",
      type: "actions",
      headerName: "",
      width: 90,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditOutlinedIcon />}
          label="Редактировать"
          onClick={() => openEdit(params.row)}
        />,
        <GridActionsCellItem
          icon={<DeleteOutlineIcon />}
          label="Удалить"
          onClick={() => setDeleteTarget(params.row)}
        />,
      ],
    },
  ];

  return (
    <div className={classNames(b(), className)}>
      <AppHeader />

      <Container maxWidth="xl" sx={{paddingTop: "48px"}}>
        <div className={b("toolbar")}>
          <OutlinedInput
            placeholder="Поиск по названию..."
            size="small"
            sx={{width: "300px"}}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <IconButton
            aria-label="Фильтры"
            onClick={() => setFiltersOpen((prev) => !prev)}
          >
            <TuneIcon />
          </IconButton>
          {hasFilters && (
            <IconButton aria-label="Сбросить фильтры" onClick={clearFilters}>
              <HighlightOffIcon />
            </IconButton>
          )}

          <div className={b("toolbar-spacer")} />

          <Button variant="contained" onClick={openCreate}>
            Создать баннер
          </Button>
        </div>

        {filtersOpen && (
          <div className={b("filters")}>
            <div className={b("filters-row")}>
              <AppSelect
                value={placement}
                label="Размещение"
                options={BANNER_PLACEMENTS}
                onChange={(e: any) => {
                  setPaginationModel((prev) => ({...prev, page: 0}));
                  setPlacement(e.target.value);
                }}
              />
              <AppSelect
                value={platform}
                label="Платформа"
                options={BANNER_PLATFORMS}
                onChange={(e: any) => {
                  setPaginationModel((prev) => ({...prev, page: 0}));
                  setPlatform(e.target.value);
                }}
              />
              <AppSelect
                value={status}
                label="Статус"
                options={BANNER_STATUSES}
                onChange={(e: any) => {
                  setPaginationModel((prev) => ({...prev, page: 0}));
                  setStatus(e.target.value);
                }}
              />
            </div>
          </div>
        )}

        <DataGrid
          columns={columns}
          rows={data?.items ?? []}
          getRowId={(row) => row.id as string}
          loading={isFetching}
          rowCount={data?.total ?? 0}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[PAGE_SIZE]}
          disableRowSelectionOnClick
          disableColumnMenu
          disableVirtualization
          autoHeight
          localeText={{
            noRowsLabel: isError ? "Ошибка загрузки" : "Баннеров нет",
          }}
        />
      </Container>

      <BannerFormDialog
        open={formOpen}
        banner={formBanner}
        onClose={() => setFormOpen(false)}
        onSaved={showSuccess}
      />

      <BannerCarouselSettingsDialog
        open={carouselOpen}
        onClose={() => setCarouselOpen(false)}
        onSaved={showSuccess}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Удалить баннер?"
        description={
          deleteTarget?.title
            ? `Баннер «${deleteTarget.title}» будет удалён безвозвратно.`
            : "Баннер будет удалён безвозвратно."
        }
        confirmText="Удалить"
        confirmColor="error"
        loading={deleteMutation.isPending}
        onConfirm={handleConfirmDelete}
        onClose={() => setDeleteTarget(null)}
      />

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{vertical: "bottom", horizontal: "right"}}
      >
        {snack ? (
          <Alert
            severity={snack.severity}
            variant="filled"
            onClose={() => setSnack(null)}
          >
            {snack.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </div>
  );
};
